import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useInViewAnalytics } from '@service-initiation/shared-data-access-analytics';
import { useGoToNextStep } from '@service-initiation/shared-data-access-flow-manager';
import { breakpoints } from '@service-initiation/shared-ui-breakpoints';
import {
  LeftMainContentSection,
  MainContentContainer,
  PageContainer,
} from '@service-initiation/shared-ui-page-layout';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import {
  DeliveryMethod,
  useCustomerDetailsAtom,
  useCustomerDetailsConfirmedAtom,
  useDeliveryMethodValue,
} from '@service-initiation/tech-care-data-access-store';
import {
  NavigationButtonsContainer,
  type NavigationButtonsContainerProps,
} from '@service-initiation/tech-care-ui-navigation-buttons';
import { StepTitle } from '@service-initiation/tech-care-ui-step-title';

import { CustomerDetailsForm } from './components';
import { customerDetailsFormSchema } from './schemas';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;

  ${breakpoints.smallDesktop} {
    gap: 16px;
  }
`;

const TEXT = TranslationEntries.Repairs.CustomerDetails;
const MAIL_IN_TEXT = TranslationEntries.Repairs.StoreLocator.MailIn;
export const CustomerDetailsPage = () => {
  const { t } = useTranslation();
  const [customerDetails, setCustomerDetails] = useCustomerDetailsAtom();
  const [, setCustomerDetailsConfirmed] = useCustomerDetailsConfirmedAtom();
  const deliveryMethod = useDeliveryMethodValue();

  const [, goToNextStep] = useGoToNextStep();
  const { ref: elementRef } = useInViewAnalytics('CustomerDetails');

  const navigateButtonProps: NavigationButtonsContainerProps = {
    continueButtonText:
      deliveryMethod === DeliveryMethod.MailIn
        ? t(MAIL_IN_TEXT.GetShippingLabel)
        : undefined,
    disableContinueButton:
      !customerDetailsFormSchema().isValidSync(customerDetails),
    onContinueButtonClick: () => {
      setCustomerDetails({ ...customerDetails });
      setCustomerDetailsConfirmed(true);
      goToNextStep();
    },
    pageName: 'CustomerDetails',
    showBackButton: true,
  };
  return (
    <PageContainer>
      <MainContentContainer>
        <LeftMainContentSection>
          <Container data-testid="customer-details-root" ref={elementRef}>
            <StepTitle
              data-testid="customer-details-title"
              subtitle={
                deliveryMethod === DeliveryMethod.ComeToMe && t(TEXT.SubTitle)
              }
              title={
                deliveryMethod === DeliveryMethod.ComeToMe
                  ? t(TEXT.RemoteTechTitle)
                  : t(TEXT.Title)
              }
            />
            <CustomerDetailsForm
              formState={customerDetails}
              setFormState={setCustomerDetails}
            />
          </Container>
        </LeftMainContentSection>
        <NavigationButtonsContainer {...navigateButtonProps} />
      </MainContentContainer>
    </PageContainer>
  );
};
